import React, { useEffect, useState } from "react";
import { EmployeeTimeSheet } from "../interfaces/V1Interface";
import V1BatchExportManager from "../managers/V1BatchExportManager";
import {useSiteContext} from "../SiteContext";

interface BatchExportModalProps {
  visible: boolean;
  onClose: () => void;
  supervisorName: string;
  supervisorId: string;
  exportType: "paint" | "drywall";
  startDate: string;
  endDate: string;
}

const BatchExportModal: React.FC<BatchExportModalProps> = ({
  visible,
  onClose,
  supervisorName,
  supervisorId,
  exportType,
  startDate,
  endDate
}) => {
  const [employees, setEmployees] = useState<EmployeeTimeSheet[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [batchId, setBatchId] = useState("");
  const [batchIdDD, setBatchIdDD] = useState("");
  const {
      week,
      year,
      createPopup,
      setLoading,
      setError
    } = useSiteContext();

    useEffect(() => {
        const fetchEmployees = async () => {
          if (!visible || !supervisorId) return;
      
          setLoading(true);
          try {
            const data = await V1BatchExportManager.getEmployees(
              supervisorId,
              startDate,
              endDate
            );
            
            // Sort employees by employeeID in ascending numeric order
            const sortedData = data.sort((a, b) => {
              const idA = parseInt(a.employeeID, 10);
              const idB = parseInt(b.employeeID, 10);
              return idA - idB;
            });
            
            setEmployees(sortedData);
            const allIDs = new Set(data.map((emp) => emp.employeeID));
            setSelectedEmployees(allIDs);
            setSelectAll(true);
          } catch (error) {
            console.error("Failed to fetch employees", error);
            setEmployees([]);
            setSelectedEmployees(new Set());
          } finally {
            setLoading(false);
          }
        };
      
        const getBatchIDs = async () => {
          const prefix = `${week}-${supervisorId}-${year}`;
          try {
            const generated = await V1BatchExportManager.createBatchId(prefix);
            setBatchId(generated);
            setBatchIdDD(`${generated}DD`);
          } catch (error) {
            console.error("Error generating batch ID:", error);
          }
        };
      
        fetchEmployees();
        getBatchIDs();
      }, [visible, supervisorId, startDate, endDate]);

  const handleCheckboxChange = (employeeID: string) => {
    setSelectedEmployees((prev) => {
      const updated = new Set(prev);
      updated.has(employeeID) ? updated.delete(employeeID) : updated.add(employeeID);
      setSelectAll(updated.size === employees.length);
      return updated;
    });
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedEmployees(new Set());
    } else {
      const allIDs = new Set(employees.map((emp) => emp.employeeID));
      setSelectedEmployees(allIDs);
    }
    setSelectAll(!selectAll);
  };

  const handleConfirmExport = async () => {
    if (!batchId || !selectedEmployees || selectedEmployees.size === 0) return;
  
    setLoading(true);
    try {
      const selectedIds = Array.from(selectedEmployees);
  
      const result = await V1BatchExportManager.completeBatchExport(
        batchId,
        exportType,
        supervisorId,
        startDate,
        endDate,
        selectedIds
      );
  
      console.log("Batch Export:", result);
  
      const BASE_DOWNLOAD_URL = "https://admin.api.wcdp.com";
  
      const regularPath = result.reports.regularReport.data.replace(/\\/g, '/');
      const ddPath = result.reports.ddReport.data.replace(/\\/g, '/');
  
      // Raw URLs (with potential spaces)
      const regularLink = `${BASE_DOWNLOAD_URL}${regularPath}`;
      const ddLink = `${BASE_DOWNLOAD_URL}${ddPath}`;
  
      // Combine into a single string with newlines between full URLs
      const message = `Regular Report: ${regularLink}\nDirect Deposit Report: ${ddLink}`;
  
      createPopup("Export Complete!", message);
      onClose();
    } catch (error) {
      console.error("Error in batch export confirm:", error);
      setError("Failed to complete batch export.");
    } finally {
      setLoading(false);
    }
  };   

  if (!visible) return null;

  function resolveDownloadUrl(filePath: string): string {
    // Replace backslashes with forward slashes
    const cleanedPath = filePath.replace(/\\/g, '/');
  
    // Prepend the correct base path
    const fullUrl = `https://admin.api.wcdp.com/adminApi${cleanedPath}`;
  
    // Properly encode spaces and special characters
    return encodeURI(fullUrl);
  }  

  return (
    <div className="batch-export-modal-overlay" onClick={onClose}>
      <div className="batch-export-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="batch-export-title">
          Export {exportType === "paint" ? "Paint" : "Drywall"} for {supervisorName}
        </h2>

        <div className="batch-export-batch-ids">
            <div className="batch-export-field">
                <label htmlFor="batch-id">Batch ID</label>
                <input
                id="batch-id"
                type="text"
                value={batchId}
                readOnly
                className="batch-export-textbox"
                />
            </div>
            <div className="batch-export-field">
                <label htmlFor="batch-id-dd">Batch ID (Direct Deposit)</label>
                <input
                id="batch-id-dd"
                type="text"
                value={batchIdDD}
                readOnly
                className="batch-export-textbox"
                />
            </div>
        </div>

        <div className="batch-export-select-all">
            <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAllChange}
            id="batch-export-select-all"
            />
            <label htmlFor="batch-export-select-all">Select All</label>
        </div>

        <ul className="batch-export-employee-list">
            {employees.map((emp) => (
            <li key={emp.employeeID} className="batch-export-employee-item">
                <label>
                <input
                    type="checkbox"
                    checked={selectedEmployees.has(emp.employeeID)}
                    onChange={() => handleCheckboxChange(emp.employeeID)}
                />
                {emp.name} (ID: {emp.employeeID})
                </label>
            </li>
            ))}
        </ul>

        <div className="batch-export-modal-footer">
            <button className="batch-export-button" onClick={handleConfirmExport}>
                Confirm Export
            </button>
            <button className="batch-export-cancel" onClick={onClose}>
                Cancel
            </button>
        </div>
      </div>
    </div>
  );
};

export default BatchExportModal;


// Add CSS styles directly in this file using a template literal
const styles = `
:root {
  --primary-color: #007bff;
  --primary-color-hover: #0056b3;
  --secondary-color: #f9f9f9;
}

.batch-export-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.batch-export-modal {
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.batch-export-title {
  margin-top: 0;
  font-size: 24px;
  color: #333;
}

.batch-export-text {
  font-size: 16px;
  margin: 20px 0;
  color: #555;
}

.batch-export-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.batch-export-confirm {
  background-color: var(--primary-color);
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.batch-export-confirm:hover {
  background-color: var(--primary-color-hover);
}

.batch-export-cancel {
  background-color: #ccc;
  color: black;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.batch-export-cancel:hover {
  background-color: #999;
}

.batch-export-loading,
.batch-export-no-employees {
  color: #555;
  font-size: 14px;
  margin-top: 15px;
}

.batch-export-employee-list {
  list-style: none;
  padding: 0;
  margin: 10px 0 20px 0;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  scrollbar-width: thin;
}

.batch-export-employee-item {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: #333;
}

.batch-export-employee-item:last-child {
  border-bottom: none;
}

.batch-export-select-all {
  margin: 10px 0;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.batch-export-batch-ids {
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.batch-export-field {
  display: flex;
  flex-direction: column;
}

.batch-export-textbox {
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  background-color: var(--secondary-color);
  color: #333;
}

.batch-export-modal-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

.batch-export-button {
  padding: 10px 20px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
}

.batch-export-button:hover {
  background-color: var(--primary-color-hover);
}

.batch-export-cancel {
  padding: 10px 20px;
  background-color: #ccc;
  color: black;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
`;

const styleTag = document.createElement('style');
styleTag.innerHTML = styles;
document.head.appendChild(styleTag);
